import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { InputText } from "primereact/inputtext";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { Chip } from "primereact/chip";
import { peicarLogoSVG } from "../../../../Assets/Images";
import { adminPanelCSS } from "../../AdminPanel.css";
import axios from "axios";
import { url } from "../../../../env";
import { Loader } from "../../../../Components";
import ModalGral from "../../../../Components/ModalGral/ModalGral";

const validatePassword = (password, type) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);

    if (password.length < minLength) {
        return "La contraseña debe tener al menos 8 caracteres";
    }
    if (!hasUpperCase) {
        return "La contraseña debe tener al menos una letra mayúscula";
    }
    if (!hasLowerCase) {
        return "La contraseña debe tener al menos una letra minúscula";
    }
    if (!hasNumber && type === 2) {
        return "La contraseña debe tener al menos un número";
    }
    return true;
};

// Componente para mostrar el estado de cada tipo de persona
const PersonTypeCard = ({
    typePerson,
    label,
    expirationData,
    loading,
    onRefresh,
    onChangePassword,
    expanded,
    onToggleExpand
}) => {
    const [daysRemaining, setDaysRemaining] = useState(null);
    const [bgColor, setBgColor] = useState("bg-green-500");
    const [needsChange, setNeedsChange] = useState(false);

    const hasError = expirationData?.hasError || false;
    const errorMessage = expirationData?.errorMessage || expirationData?.message || '';

    useEffect(() => {
        if (expirationData?.fechaExp && !hasError) {
            const today = new Date();
            const [month, day, year] = expirationData.fechaExp.split("/").map(Number);
            const expirationDate = new Date(year, month - 1, day);
            const diffTime = expirationDate.getTime() - today.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            setDaysRemaining(diffDays - 1);

            if (diffDays <= 5) {
                setBgColor("bg-red-500");
                setNeedsChange(true);
            } else if (diffDays <= 15) {
                setBgColor("bg-yellow-500");
                setNeedsChange(false);
            } else {
                setBgColor("bg-green-500");
                setNeedsChange(false);
            }
        } else if (hasError) {
            setDaysRemaining(null);
            setBgColor("bg-gray-500");
            setNeedsChange(false);
        }
    }, [expirationData, hasError]);

    const getStatusMessage = () => {
        if (hasError) return errorMessage || "Error al obtener datos";
        if (loading) return "Cargando...";
        if (daysRemaining === null) return "Sin datos";
        if (daysRemaining <= 5) return "¡Se recomienda cambiar la contraseña ahora!";
        if (daysRemaining <= 15) return "Próximo a vencer";
        return "Sin cambios necesarios";
    };

    const getBorderColor = () => {
        if (hasError) return '#6b7280'; // gray
        if (daysRemaining === null) return '#9ca3af'; // light gray
        if (daysRemaining <= 5) return '#ef4444'; // red
        if (daysRemaining <= 15) return '#eab308'; // yellow
        return '#22c55e'; // green
    };

    return (
        <Card
            className="mb-3 shadow-2"
            style={{
                borderLeft: `4px solid ${getBorderColor()}`,
                background: 'var(--surface-card)'
            }}
        >
            <div className="flex flex-column gap-3">
                {/* Header */}
                <div className="flex justify-content-between align-items-center">
                    <div className="flex align-items-center gap-3">
                        <i className={`pi ${typePerson === 'pf' ? 'pi-user' : 'pi-building'}`}
                            style={{ fontSize: '1.5rem', color: hasError ? '#6b7280' : 'var(--primary-color)' }}></i>
                        <div>
                            <h3 className="m-0 text-lg font-semibold">{label}</h3>
                            <span className="text-sm text-color-secondary">
                                {expirationData?.member || 'Sin usuario'}
                            </span>
                        </div>
                    </div>
                    <Button
                        icon="pi pi-refresh"
                        className="p-button-text p-button-rounded"
                        onClick={() => onRefresh(typePerson)}
                        loading={loading}
                        tooltip="Actualizar"
                    />
                </div>

                {/* Error Message */}
                {hasError && (
                    <div className="p-3 border-round" style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
                        <div className="flex align-items-center gap-2">
                            <i className="pi pi-exclamation-triangle" style={{ color: '#dc2626' }}></i>
                            <span style={{ color: '#dc2626' }}>{errorMessage}</span>
                        </div>
                    </div>
                )}

                {/* Status */}
                {!hasError && (
                    <div className="flex flex-wrap gap-2 align-items-center">
                        {daysRemaining !== null ? (
                            <>
                                <Chip
                                    label={
                                        <span>
                                            <strong>{daysRemaining}</strong> días restantes
                                        </span>
                                    }
                                    className={`${bgColor} text-white`}
                                />
                                <Chip
                                    label={getStatusMessage()}
                                    className={`${needsChange ? 'bg-red-700' : 'bg-gray-300'} text-white`}
                                />
                            </>
                        ) : (
                            <Chip label={loading ? "Cargando..." : "Sin datos"} className="bg-gray-300" />
                        )}
                    </div>
                )}

                {/* Fecha de expiración */}
                {expirationData?.fechaExp && !hasError && (
                    <div className="text-sm text-color-secondary">
                        <i className="pi pi-calendar mr-2"></i>
                        Fecha de expiración: <strong>{expirationData.fechaExp}</strong>
                    </div>
                )}

                {/* Botón para expandir/colapsar formulario de cambio */}
                <div className="flex gap-2">
                    <Button
                        label={expanded ? "Ocultar formulario" : "Cambiar contraseña"}
                        icon={expanded ? "pi pi-chevron-up" : "pi pi-key"}
                        className={`p-button-sm ${needsChange ? 'p-button-danger' : hasError ? 'p-button-secondary' : 'p-button-outlined'}`}
                        onClick={() => onToggleExpand(typePerson)}
                        disabled={!expirationData?.member}
                    />
                </div>

                {/* Formulario expandible */}
                {expanded && expirationData?.member && (
                    <ChangePasswordForm
                        typePerson={typePerson}
                        expirationData={expirationData}
                        onChangePassword={onChangePassword}
                    />
                )}
            </div>
        </Card>
    );
};

// Formulario para cambiar contraseña
const ChangePasswordForm = ({ typePerson, expirationData, onChangePassword }) => {
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const { register, handleSubmit, setValue, formState: { errors }, reset } = useForm({
        defaultValues: {
            usuario: expirationData?.member || '',
            passwordOld: '',
            passwordNew: '',
        },
    });

    useEffect(() => {
        if (expirationData?.member) {
            setValue('usuario', expirationData.member);
        }
    }, [expirationData, setValue]);

    const validatePasswordOld = (password) => validatePassword(password, 1);
    const validatePasswordNew = (password) => validatePassword(password, 2);

    const submit = async (data) => {
        setLoading(true);
        try {
            const result = await onChangePassword({
                member: data.usuario,
                password: data.passwordOld,
                newPassword: data.passwordNew,
                typePerson: typePerson
            });
            if (result) {
                reset({ usuario: expirationData?.member || '', passwordOld: '', passwordNew: '' });
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit(submit)} className="p-3 surface-ground border-round">
            <div className="grid">
                <div className="col-12">
                    <label className="block text-sm font-medium mb-1">Usuario</label>
                    <InputText
                        maxLength={40}
                        {...register("usuario", { required: "El usuario es obligatorio" })}
                        placeholder="Usuario"
                        className={`w-full ${errors.usuario ? "p-invalid" : ""}`}
                        disabled
                    />
                    {errors.usuario && <span className="p-error text-sm">{errors.usuario.message}</span>}
                </div>

                <div className="col-12 md:col-6">
                    <label className="block text-sm font-medium mb-1">Contraseña actual</label>
                    <IconField iconPosition="right">
                        <div className="p-inputgroup">
                            <InputText
                                type={showPassword ? "text" : "password"}
                                {...register("passwordOld", {
                                    required: "La contraseña actual es obligatoria",
                                    validate: validatePasswordOld
                                })}
                                placeholder="Contraseña actual"
                                className={`w-full ${errors.passwordOld ? "p-invalid" : ""}`}
                            />
                            <InputIcon
                                onClick={() => setShowPassword(!showPassword)}
                                style={{ position: "absolute", right: "1em", top: "50%", transform: "translateY(-50%)", cursor: "pointer" }}
                                className={showPassword ? "pi pi-eye-slash" : "pi pi-eye"}
                            />
                        </div>
                    </IconField>
                    {errors.passwordOld && <span className="p-error text-sm">{errors.passwordOld.message}</span>}
                </div>

                <div className="col-12 md:col-6">
                    <label className="block text-sm font-medium mb-1">Nueva contraseña</label>
                    <IconField iconPosition="right">
                        <div className="p-inputgroup">
                            <InputText
                                type={showPassword ? "text" : "password"}
                                {...register("passwordNew", {
                                    required: "La nueva contraseña es obligatoria",
                                    validate: validatePasswordNew
                                })}
                                placeholder="Nueva contraseña"
                                className={`w-full ${errors.passwordNew ? "p-invalid" : ""}`}
                            />
                            <InputIcon
                                onClick={() => setShowPassword(!showPassword)}
                                style={{ position: "absolute", right: "1em", top: "50%", transform: "translateY(-50%)", cursor: "pointer" }}
                                className={showPassword ? "pi pi-eye-slash" : "pi pi-eye"}
                            />
                        </div>
                    </IconField>
                    {errors.passwordNew && <span className="p-error text-sm">{errors.passwordNew.message}</span>}
                </div>

                <div className="col-12">
                    <Button
                        type="submit"
                        label="Cambiar contraseña"
                        icon="pi pi-check"
                        className="w-full p-button-success"
                        loading={loading}
                    />
                </div>
            </div>
        </form>
    );
};

// Componente principal
export const ChangePassword = ({ toast }) => {
    const [loading, setLoading] = useState(false);
    const [loadingPf, setLoadingPf] = useState(false);
    const [loadingPm, setLoadingPm] = useState(false);
    const [expirationDataPf, setExpirationDataPf] = useState({});
    const [expirationDataPm, setExpirationDataPm] = useState({});
    const [expandedPf, setExpandedPf] = useState(false);
    const [expandedPm, setExpandedPm] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const newPass = useRef(null);

    // Función para obtener fecha de expiración por tipo
    const getExpirationDateFunc = async (typePerson) => {
        const setLoadingFunc = typePerson === 'pf' ? setLoadingPf : setLoadingPm;
        const setDataFunc = typePerson === 'pf' ? setExpirationDataPf : setExpirationDataPm;

        setLoadingFunc(true);
        try {
            const response = await axios.post(`${url}/getExpirationDate`,
                { typePerson: typePerson },
                {
                    headers: {
                        "Authorization": `Bearer ${localStorage.getItem('token')}`,
                    },
                }
            );

            // Verificar si la respuesta contiene error
            if (response.data.error) {
                setDataFunc({
                    ...response.data,
                    hasError: true,
                    errorMessage: response.data.message
                });
                toast?.current?.show({
                    severity: "warn",
                    summary: "Advertencia",
                    detail: response.data.message || `Error al obtener datos de ${typePerson === 'pf' ? 'Persona Física' : 'Persona Moral'}`,
                    life: 5000,
                });
            } else {
                setDataFunc({
                    ...response.data,
                    hasError: false
                });
            }
        } catch (error) {
            console.error(`Error obteniendo fecha de expiración para ${typePerson}:`, error);

            // Guardar datos de error del backend si existen
            const errorData = error.response?.data || {};
            setDataFunc({
                hasError: true,
                errorMessage: errorData.message || 'Error de conexión',
                member: errorData.member || null,
                typePerson: typePerson,
                errorType: errorData.errorType || 'connection_error'
            });

            toast?.current?.show({
                severity: "error",
                summary: "Error",
                detail: errorData.message || `No se pudo obtener la fecha de expiración para ${typePerson === 'pf' ? 'Persona Física' : 'Persona Moral'}`,
                life: 5000,
            });
        } finally {
            setLoadingFunc(false);
        }
    };

    // Función para cambiar contraseña
    const handleChangePassword = async (data) => {
        try {
            const res = await axios.post(`${url}/changePassworBC`, {
                member: data.member,
                password: data.password,
                newPassword: data.newPassword,
                typePerson: data.typePerson
            }, {
                headers: {
                    "Authorization": `Bearer ${localStorage.getItem('token')}`,
                },
            });

            toast?.current?.show({
                severity: 'success',
                summary: 'Éxito',
                detail: `Contraseña cambiada con éxito para ${data.typePerson === 'pf' ? 'Persona Física' : 'Persona Moral'}`,
                life: 3000
            });

            // Actualizar datos después del cambio
            await getExpirationDateFunc(data.typePerson);

            newPass.current = res.data.password;
            setShowModal(true);

            return true;
        } catch (error) {
            console.error(error);
            toast?.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudo cambiar la contraseña',
                life: 3000
            });
            return false;
        }
    };

    // Toggle para expandir/colapsar formularios
    const handleToggleExpand = (typePerson) => {
        if (typePerson === 'pf') {
            setExpandedPf(!expandedPf);
        } else {
            setExpandedPm(!expandedPm);
        }
    };

    // Cargar datos iniciales
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            await Promise.all([
                getExpirationDateFunc('pf'),
                getExpirationDateFunc('pm')
            ]);
            setLoading(false);
        };
        fetchData();
    }, []);

    return (
        <section css={adminPanelCSS()}>
            {loading && <Loader />}

            <div className="p-4">
                <div className="flex align-items-center gap-3 mb-4">
                    <h2 className="m-0">Gestión de Contraseñas - Buró de Crédito</h2>
                </div>

                <div className="grid">
                    <div className="col-12 lg:col-6">
                        <PersonTypeCard
                            typePerson="pf"
                            label="Persona Física"
                            expirationData={expirationDataPf}
                            loading={loadingPf}
                            onRefresh={getExpirationDateFunc}
                            onChangePassword={handleChangePassword}
                            expanded={expandedPf}
                            onToggleExpand={handleToggleExpand}
                        />
                    </div>

                    <div className="col-12 lg:col-6">
                        <PersonTypeCard
                            typePerson="pm"
                            label="Persona Moral"
                            expirationData={expirationDataPm}
                            loading={loadingPm}
                            onRefresh={getExpirationDateFunc}
                            onChangePassword={handleChangePassword}
                            expanded={expandedPm}
                            onToggleExpand={handleToggleExpand}
                        />
                    </div>
                </div>

                {showModal && (
                    <ModalGral
                        showModal={showModal}
                        setShowModal={setShowModal}
                        admin={true}
                        data={newPass}
                        toast={toast}
                    />
                )}
            </div>
        </section>
    );
};

export default ChangePassword;
